import { Link } from "react-router-dom";
import { Wallet, ArrowLeft } from "lucide-react";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-semibold mb-4">{title}</h2>
    <div className="space-y-3 text-gray-300 leading-relaxed">{children}</div>
  </section>
);

const PaymentsPolicy = () => {
  const lastUpdated = "September 3, 2026";

  return (
    <div className="min-h-screen bg-black text-white">
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Metro City Groove
        </Link>

        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Wallet className="w-10 h-10 text-purple-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Payments &amp; Payouts Policy</h1>
          </div>
          <p className="text-sm text-gray-400">Last Updated: {lastUpdated}</p>
        </div>

        <p className="text-lg text-gray-300 leading-relaxed mb-10">
          This policy explains how money moves on Metro City Groove, operated by Metro City Trading (Pty) Ltd. It covers
          subscriptions, ticket sales, merchandise, bookings, gifts, affiliate commissions, payouts, refunds and fees.
          All amounts are in South African Rand (ZAR) unless stated otherwise.
        </p>

        <Section title="1. Payment processing">
          <p>
            Card payments, EFT and instant transfers are processed by our payment provider (Paystack). Metro City Groove
            does not store full card details. A transaction is only treated as complete once the provider confirms it and
            our webhook records the order as paid.
          </p>
          <p>
            Failed, reversed or unverified transactions never unlock paid features, tickets or earnings, even if a
            checkout page was completed.
          </p>
        </Section>

        <Section title="2. Subscriptions (Creator Pro and Venue Pro)">
          <ul className="list-disc pl-5 space-y-2">
            <li>Signing up is free. Groover accounts always remain free.</li>
            <li>Creator Pro is R249 per month; Venue Pro is R849 per month, billed monthly in advance.</li>
            <li>Subscriptions renew automatically until cancelled from the in-app billing page.</li>
            <li>Cancellation takes effect at the end of the paid period — access continues until that date, and no partial refunds are issued for unused days.</li>
            <li>Failed renewals downgrade the account to Groover access until payment succeeds.</li>
          </ul>
        </Section>

        <Section title="3. Ticket sales and event revenue">
          <ul className="list-disc pl-5 space-y-2">
            <li>Ticket revenue is credited to the wallet of the event owner (the venue, or the creator who hosts the event under a venue booking) once the order is confirmed paid.</li>
            <li>Service fees shown at checkout are disclosed to the buyer before payment.</li>
            <li>Age-restricted events require verified date-of-birth information before a ticket can be issued.</li>
            <li>QR tickets may be single-admit or multi-admit (packages). Scan counts are final once recorded at the door.</li>
            <li>If an event is cancelled by the organiser, ticket buyers are refunded from the organiser's balance; the organiser is responsible for any shortfall.</li>
          </ul>
        </Section>

        <Section title="4. Bookings and collaboration payments">
          <p>
            Booking fees agreed between a venue and a creator (or between creators) are payable through the platform so
            that both parties have a record. Detailed obligations are set out in the{" "}
            <Link to="/collaboration-terms" className="text-purple-400 hover:text-purple-300">Collaboration &amp; Work Terms</Link>.
          </p>
        </Section>

        <Section title="5. Gifts and tips">
          <p>
            Gifts sent during live streams or on profiles are voluntary, non-refundable payments. The value, less
            processing and platform fees, is credited to the recipient's earnings balance. Gifts are not a purchase of
            goods or services and create no obligation on the recipient.
          </p>
        </Section>

        <Section title="6. Affiliate commissions">
          <ul className="list-disc pl-5 space-y-2">
            <li>The affiliate programme is available to verified Creator and Venue accounts with a confirmed email address.</li>
            <li>Affiliates earn 30% commission on the first month paid by each referred creator or venue.</li>
            <li>Referred accounts receive 30% off their first month.</li>
            <li>Commission is confirmed only after the referred subscription payment clears and the refund window passes.</li>
            <li>Self-referrals, duplicate accounts, misleading advertising and paid search on our brand names void commission.</li>
            <li>Commission is reversed if the referred payment is refunded or charged back.</li>
          </ul>
        </Section>

        <Section title="7. Payouts">
          <ul className="list-disc pl-5 space-y-2">
            <li>Only funds from confirmed paid orders count toward a withdrawable balance.</li>
            <li>Payouts are requested from the Wallet or Payouts page and are paid to a verified bank account in the account holder's own name or registered business name.</li>
            <li>A minimum withdrawal threshold applies, and requests are reviewed for fraud and compliance before release.</li>
            <li>Payouts may be held where verification is incomplete, where a dispute or chargeback is open, or where the law requires it.</li>
            <li>Bank charges, reversal fees and costs caused by incorrect banking details are for the account holder.</li>
          </ul>
        </Section>

        <Section title="8. Refunds, disputes and chargebacks">
          <ul className="list-disc pl-5 space-y-2">
            <li>Digital access, subscriptions, gifts and completed services are generally non-refundable.</li>
            <li>Tickets are refundable when an event is cancelled or materially changed by the organiser.</li>
            <li>Disputes should be raised through Support Center first; we may hold the disputed amount while investigating.</li>
            <li>Chargebacks are deducted from the recipient's balance, together with any provider penalty.</li>
          </ul>
        </Section>

        <Section title="9. Taxes and compliance">
          <p>
            Creators, venues, agencies and affiliates are independent parties and are solely responsible for their own
            income tax, VAT registration and reporting obligations. Businesses must supply valid registration details
            (for example CIPC in South Africa) where verification is required.
          </p>
        </Section>

        <Section title="10. Fraud, suspension and recovery">
          <p>
            We may suspend payouts, reverse credits and close accounts where we reasonably suspect fraud, money
            laundering, ticket touting, fake events or abuse of the affiliate programme, and we may recover amounts
            already paid out in those circumstances.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            Payment questions: <a href="mailto:support@metrocitytrading.co.za" className="text-purple-400 hover:text-purple-300">support@metrocitytrading.co.za</a>.
            See also the <Link to="/terms" className="text-purple-400 hover:text-purple-300">Terms of Service</Link> and{" "}
            <Link to="/privacy" className="text-purple-400 hover:text-purple-300">Privacy Policy</Link>.
          </p>
        </Section>
      </main>
    </div>
  );
};

export default PaymentsPolicy;
